/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import '@formatjs/intl-getcanonicallocales/polyfill';

import '@formatjs/intl-locale/polyfill';

import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/es';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/de';
import '@formatjs/intl-pluralrules/locale-data/it';
import '@formatjs/intl-pluralrules/locale-data/pt';

import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/es';
import '@formatjs/intl-numberformat/locale-data/fr';
import '@formatjs/intl-numberformat/locale-data/de';
import '@formatjs/intl-numberformat/locale-data/it';
import '@formatjs/intl-numberformat/locale-data/pt';

import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/en';
import '@formatjs/intl-relativetimeformat/locale-data/es';
import '@formatjs/intl-relativetimeformat/locale-data/fr';
import '@formatjs/intl-relativetimeformat/locale-data/de';
import '@formatjs/intl-relativetimeformat/locale-data/it';
import '@formatjs/intl-relativetimeformat/locale-data/pt';

import translationsEnUs from '../locales/en-US.json';
import translationsEsEs from '../locales/es-ES.json';
import translationsFrFr from '../locales/fr-FR.json';
import translationsDeDe from '../locales/de-DE.json';
import translationsItIt from '../locales/it-IT.json';
import translationsPtBr from '../locales/pt-BR.json';
import countriesEn from 'i18n-iso-countries/langs/en.json';
import countriesEs from 'i18n-iso-countries/langs/es.json';
import countriesFr from 'i18n-iso-countries/langs/fr.json';
import countriesDe from 'i18n-iso-countries/langs/de.json';
import countriesIt from 'i18n-iso-countries/langs/it.json';
import countriesPt from 'i18n-iso-countries/langs/pt.json';
import countries from 'i18n-iso-countries';
import { Product } from '../context/HandoverParams';

const enUs = 'en-US';
const deDe = 'de-DE';
const esEs = 'es-ES';
const frFr = 'fr-FR';
const itIt = 'it-IT';
const ptBr = 'pt-BR';
// a '.toLowerCase()' would make the type a string
const enUsLower = 'en-us';
const deDeLower = 'de-de';
const esEsLower = 'es-es';
const frFrLower = 'fr-fr';
const itItLower = 'it-it';
const ptBrLower = 'pt-br';

countries.registerLocale(countriesEn);
countries.registerLocale(countriesEs);
countries.registerLocale(countriesFr);
countries.registerLocale(countriesDe);
countries.registerLocale(countriesIt);
countries.registerLocale(countriesPt);

const defaultLocale = enUs;
const locales = {
  en: enUs,
  de: deDe,
  es: esEs,
  fr: frFr,
  it: itIt,
  pt: ptBr,
};

type Locale =
  | typeof enUsLower
  | typeof esEsLower
  | typeof frFrLower
  | typeof deDeLower
  | typeof itItLower
  | typeof ptBrLower;

const getShortLocale = (locale: string): null | keyof typeof locales => {
  const prefix = locale.substr(0, 2).toLowerCase();
  if (Object.keys(locales).indexOf(prefix) !== -1) {
    return prefix as keyof typeof locales;
  }

  return null;
};

const getHyphenedLocale = (locale: string): string => {
  if (locale) {
    const normalized = getShortLocale(locale);

    if (normalized) {
      return locales[normalized];
    }
  }

  return '';
};

export type ProductTranslation = {
  [product in Product]: string;
};

export const getProductTranslation = (
  translationValue: string | ProductTranslation,
  product: Product
): string => {
  if (typeof translationValue === 'string') {
    return translationValue;
  } else {
    return translationValue[product];
  }
};

export const getProductSpecificLocalObject = (
  rawTranslationFile: RawTranslationFile,
  product: Product
): TranslationFile => {
  rawTranslationFile = JSON.parse(JSON.stringify(rawTranslationFile));
  Object.entries(rawTranslationFile).forEach(([key, value]) => {
    rawTranslationFile[key] = getProductTranslation(value, product);
  });
  return rawTranslationFile as TranslationFile;
};

export interface TranslationFile {
  [id: string]: string;
}

export interface RawTranslationFile {
  [id: string]: string | ProductTranslation;
}

export const getTranslationsForLocale = (
  locale: string,
  product: Product = Product.G2M
) => {
  const translations: { [lang in Locale]: RawTranslationFile } = {
    [enUsLower]: translationsEnUs,
    [esEsLower]: translationsEsEs,
    [frFrLower]: translationsFrFr,
    [deDeLower]: translationsDeDe,
    [itItLower]: translationsItIt,
    [ptBrLower]: translationsPtBr,
  };
  const languageData = translations[locale.toLowerCase() as Locale];
  if (languageData === undefined) throw new ReferenceError();
  return getProductSpecificLocalObject(languageData, product);
};

const Translations = [
  enUsLower,
  esEsLower,
  frFrLower,
  deDeLower,
  itItLower,
  ptBrLower,
];

export const getLocale = () => {
  const preferredLocale = getHyphenedLocale(navigator.language);

  return Translations.includes(preferredLocale.toLowerCase())
    ? preferredLocale
    : defaultLocale;
};
